/**
 * Phoenix LiveView hook for animated segmented control tabs.
 * Handles the sliding animation of the background pill when tabs are clicked.
 */

import { Hook, makeHook } from './Hook'

export default makeHook(
  class SegmentedControlTabs extends Hook {
    backgroundPill: HTMLElement | null = null
    tabs: NodeListOf<HTMLButtonElement> = null as unknown as NodeListOf<HTMLButtonElement>
    totalTabs: number = 0
    handleTabClick: (event: Event) => void = null as unknown as (event: Event) => void

    mounted(): void {
      this.backgroundPill = this.el.querySelector('[data-background-pill]') as HTMLElement | null
      this.tabs = this.el.querySelectorAll('button[data-tab-index]') as NodeListOf<HTMLButtonElement>
      this.totalTabs = parseInt(this.el.dataset.totalTabs || '0', 10)

      // Handle clicks on tabs to animate the background pill
      this.handleTabClick = (event: Event): void => {
        const clickedTab = event.currentTarget as HTMLButtonElement
        const tabIndex = parseInt(clickedTab.dataset.tabIndex || '0', 10)

        if (this.backgroundPill && !isNaN(tabIndex)) {
          this.animateToTab(tabIndex)
        }
      }

      // Add click listeners to all tabs
      this.tabs.forEach((tab: HTMLButtonElement): void => {
        tab.addEventListener('click', this.handleTabClick)
      })
    }

    updated(): void {
      // Update the background pill position when the component updates
      const activeIndex = parseInt(this.el.dataset.activeIndex || '0', 10)
      const totalTabs = parseInt(this.el.dataset.totalTabs || '0', 10)

      if (this.backgroundPill && !isNaN(activeIndex)) {
        this.animateToTab(activeIndex)
      }

      // Update total tabs if it changed
      this.totalTabs = totalTabs
    }

    animateToTab(index: number): void {
      if (!this.backgroundPill || isNaN(index) || this.totalTabs === 0) {
        return
      }

      // Apply the transform and width
      this.backgroundPill.style.transform = `translateX(${this.el.dataset.translateXAmount || '0px'})`
      this.backgroundPill.style.width = this.el.dataset.widthStyle || 'auto'
    }

    destroyed(): void {
      // Clean up event listeners
      if (this.tabs) {
        this.tabs.forEach((tab: HTMLButtonElement): void => {
          tab.removeEventListener('click', this.handleTabClick)
        })
      }
    }
  }
)
